"use client";

import { useAuth } from "@/context/AuthContext";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { UserMenu } from "./UserMenu";
import { Logo } from "@/components/ui/Logo";

export function ClientLayout({ children }: { children: React.ReactNode }) {
    const { user, loading, logout } = useAuth();
    const pathname = usePathname();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    // If user is NOT logged in, just render children (Landing, Login, Signup)
    if (!user) {
        return <>{children}</>;
    }

    // If user IS logged in, render Dashboard Layout (Header + Content)
    // OPTIONAL: If we are effectively on the landing page `/`, maybe we still want to show the specific Landing Page?
    // But typically SaaS redirects / to /dashboard if logged in. 
    // For now, we will wrap everything in the dashboard header if logged in.

    return (
        <>
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Logo width={140} height={35} href="/" />
                        <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-2 hidden lg:block" />
                        <Link href="/dashboard" className="hidden lg:flex items-center gap-2 hover:opacity-80 transition-opacity">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Workspace</span>
                            <span className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-800 text-sm font-medium">
                                {user.name}'s Workspace
                            </span>
                        </Link>
                    </div>
                    <nav className="flex items-center gap-6">
                        <Link
                            href="/dashboard"
                            className={`hidden md:block text-sm font-semibold transition-colors ${pathname === '/dashboard' ? 'text-blue-600' : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'}`}
                        >
                            Interviews
                        </Link>
                        <Link
                            href="/dashboard/screening"
                            className={`hidden md:block text-sm font-semibold transition-colors ${pathname.startsWith('/dashboard/screening') ? 'text-blue-600' : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'}`}
                        >
                            CV Screening
                        </Link>
                        <div className="h-4 w-px bg-slate-200 dark:bg-slate-800 hidden sm:block" />
                        <UserMenu />
                    </nav>
                </div>
            </header>
            <div className="pt-16">
                {children}
            </div>
        </>
    );
}
